import { MantineProvider } from '@mantine/core';
import { PropsWithChildren, useEffect } from 'react';
import tinycolor from 'tinycolor2';
import { useTranslation } from 'react-i18next';

import { LemonadeProvider } from '@percent/lemonade';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { usePartnerConfig } from '@percent/hosted-validation-form/hooks';
import GoodstackLogo from '@percent/hosted-validation-form/assets/icons/goodstack-page-logo.svg';

const defaultColors = {
  primaryColor: '#EAEAFE',
};

// Exactly 10 elements required by Mantine
const fillColor = (color: string) => [color, color, color, color, color, color, color, color, color, color] as const;

const getPrimaryAccent = (color: string) =>
  tinycolor(color).isDark() ? tinycolor(color).brighten(15).toString() : tinycolor(color).darken(15).toString();

export function ThemeProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const { partnerConfig } = usePartnerConfig();
  const partnerColor = partnerConfig?.configuration.primaryColor || defaultColors.primaryColor;
  const isPartnerColorDark = tinycolor(partnerColor).isDark();

  useEffect(() => {
    if (partnerConfig) {
      const favicon = document.getElementById('favicon') as HTMLLinkElement;
      const description = document.getElementById('description') as HTMLMetaElement;

      if (favicon && document && description) {
        favicon.href = partnerConfig?.configuration.logo ?? GoodstackLogo;
        document.title = t('hostedValidation.site.title', { partner: partnerConfig?.partner.name });
        description.content = t('hostedValidation.site.contentApply', {
          partnerName: partnerConfig?.partner.name,
        });
      }
    }
  }, [partnerConfig, t]);

  const ctaTextColor = isPartnerColorDark ? '#FFFFFF' : '#000000';

  return (
    <MantineProvider
      theme={{
        primaryColor: 'primary',
        colors: {
          primary: fillColor(partnerColor),
          secondary: fillColor(partnerColor),
        },
        white: ctaTextColor,
      }}
    >
      <LemonadeProvider
        customPalette={{
          primary400: partnerColor,
          primary600: getPrimaryAccent(partnerColor),
          white: ctaTextColor,
        }}
      >
        {children}
      </LemonadeProvider>
    </MantineProvider>
  );
}
